<template>
    <IonHeader
        class="ion-no-border"
        data-cy="filters-modal"
    >
        <div class="w-full font-medium text-md border-b border-b-transparent transition-colors duration-300 py-2">
            <div class="w-full flex items-center justify-between px-6 pr-20 overflow-hidden">
                <div class="w-[50px] shrink">
                    <div
                        v-if="activeGroup"
                        data-cy="close-group-btn"
                        class="w-[32px] h-[38px] flex items-center justify-center rounded-full"
                        @click="setGroup(null)"
                    >
                        <PhIcon
                            v-if="isIos"
                            icon="caret-left"
                            weight="regular"
                            :size="24"
                        />
                        <PhIcon
                            v-else
                            icon="arrow-left"
                            weight="regular"
                            :size="24"
                        />
                    </div>
                    <div
                        v-else
                        class="w-[32px] h-[38px] flex items-center justify-center rounded-full"
                        @click="cancel"
                    >
                        <PhIcon
                            icon="x"
                            weight="light"
                            :size="24"
                        />
                    </div>
                </div>

                <span
                    class="flex-1 truncate text-gray-900 font-medium"
                    :class="isIos ? 'text-center' : 'text-left'"
                >
                    {{ activeGroup ? activeGroup.title : 'Filter' }}
                </span>
            </div>
        </div>
    </IonHeader>
    <IonContent>
        <div v-if="activeGroup">
            <FilterTypeGroup
                :filter-uuid="filterUuid"
                :filter="activeGroup"
                :model-value="localValue[activeGroup.id]"
                @update:model-value="updateFilterValue(activeGroup, $event)"
            />
        </div>
        <div
            v-else
            class="py-3 px-6 pt-1"
        >
            <DForm>
                <div
                    v-for="filter in filtersWithComponent"
                    :key="filter.id"
                    :data-cy="`filters-modal-filter-${filter.type}`"
                >
                    <div
                        v-if="filter.type === filterType.group"
                        data-cy="group-btn"
                        class="flex items-center justify-between py-4 border-y border-neutral-base cursor-pointer"
                        @click="setGroup(filter)"
                    >
                        <div class="text-sm font-medium">
                            {{ filter.title }}
                        </div>
                        <div class="flex items-center justify-center">
                            <div
                                class="text-sm font-regular mr-2"
                                :class="{
                                    'text-neutral-extra-subtle': getGroupValues(filter).length <= 0,
                                }"
                            >
                                {{ getGroupPreview(filter) }}
                            </div>
                            <PhIcon
                                icon="caret-right"
                                class="text-gray-500"
                                :size="24"
                                weight="regular"
                            />
                        </div>
                    </div>
                    <component
                        :is="filter.component"
                        v-else-if="filter.component"
                        :filter="filter"
                        :filter-uuid="filterUuid"
                        :model-value="localValue[filter.id]"
                        @update:model-value="updateFilterValue(filter, $event)"
                    />
                </div>
            </DForm>
        </div>
    </IonContent>
    <IonFooter class="ion-no-border border-t border-neutral-base border-solid">
        <div class="flex w-full justify-between p-3">
            <DButton
                theme="transparent"
                :disabled="!canClearAll"
                data-cy="filters-modal-clear-all"
                @click="clearAll"
            >
                Clear all
            </DButton>
            <DButton
                data-cy="filters-modal-confirm"
                @click="confirm"
            >
                Show results
            </DButton>
        </div>
    </IonFooter>
</template>
<script setup>
    import { cloneDeep, keys } from 'lodash'
    import { DForm, DButton } from '@digistorm/spark'
    import { modalController } from '@ionic/vue'
    import { useFilterStore } from '@/stores/filter'
    import { useFilters } from '@/composables'
    import filterType from '@/constants/filterType'
    import PhIcon from '@/components/app/PhIcon.vue'
    import FilterTypeGroup from '@/components/filters/modal/FilterTypeGroup.vue'

    const props = defineProps({
        filterUuid: String,
        filters: Array,
    })

    const deviceStore = useDeviceStore()
    const filterStore = useFilterStore()
    const { modalComponentForFilter } = useFilters()

    const { isIos } = storeToRefs(deviceStore)

    const localValue = ref()
    const activeGroup = ref(null)

    const activeFilters = computed(() => {
        return filterStore.filtersForUuid(props.filterUuid)
    })

    const activeFilterCount = computed(() => {
        return keys(activeFilters.value).length
    })

    const filtersWithComponent = computed(() => {
        return props.filters.map((filter) => {
            return {
                ...filter,
                component: modalComponentForFilter(filter),
            }
        })
    })

    const canClearAll = computed(() => {
        return activeFilterCount.value || keys(localValue.value).length
    })

    const cancel = () => modalController.dismiss(null, 'cancel')

    const confirm = () => {
        filterStore.updateFilters(props.filterUuid, localValue.value)
        modalController.dismiss(null, 'confirm')
    }

    const clearAll = () => {
        filterStore.updateFilters(props.filterUuid, {})
        cancel()
    }

    const updateFilterValue = (filter, filterValue) => {
        if (filterValue === null) {
            delete localValue.value[filter.id]
            return
        }
        localValue.value[filter.id] = filterValue
    }

    const setGroup = (group) => {
        activeGroup.value = group
    }

    const getGroupValues = (group) => {
        return group.filters
            .map((filter) => localValue.value[group.id]?.[filter.id] ?? null)
            .filter((value) => value)
    }

    const getGroupPreview = (group) => {
        const values = getGroupValues(group)

        if (values.length <= 0) {
            return ''
        }
        if (values.length > 2) {
            return 'Filters applied'
        }
        return values.join(', ')
    }

    watch(activeFilters, () => {
        localValue.value = cloneDeep(activeFilters.value || {})
    }, { immediate: true })
</script>
